import { lazy, Suspense } from "react";
import { useAuthContext } from "src/auth/hooks";
import DashboardLayout from "src/layouts/dashboard";
import { Outlet, Navigate, useParams } from "react-router-dom";
import CompanyOnboardingPage from 'src/pages/CompanyOnboardingPage';
import CompanyOnboardingGuard from 'src/auth/guard/company-onboarding-guard';
import { isExpensesOnlySupervisor } from "@heyjob-dash/shared/utils/check-permission";
import { MODULE_FLAGS, PREVIEW_FLAGS } from "@heyjob-dash/shared/config/company-modules";
import { AuthGuard, RoleGuard, ModuleGuard, SupervisorPermissionGuard } from "src/auth/guard";

import { paths } from "src/routes/paths";

import { LoadingScreen } from "src/components/loading-screen";

import FAQPage from "../../pages/FAQPage";

// ----------------------------------------------------------------------

// OVERVIEW
const EmployeeCustomFieldsConfigPage = lazy(
  () => import("src/pages/config/custom-fields/EmployeeCustomFieldsConfigPage"),
);

const IndexPage = lazy(() => import("src/pages/HomePage"));
const EmployeesPage = lazy(() => import("src/pages/employee/AnagrafichePage"));
const ReportsPage = lazy(() => import("src/pages/report/ReportsPage"));
const EmployeesSecurityReportPage = lazy(
  () => import("src/pages/report/EmployeeSecurityReport"),
);
const EmployeesPayrollDataReportPage = lazy(
  () => import("src/pages/report/EmployeePayrollDataReport"),
);
const EmployeeDocumentsReport = lazy(
  () => import("src/pages/report/EmployeeDocumentsReport"),
);
const EmployeeCustomReport = lazy(
  () => import("src/pages/report/EmployeeCustomReport"),
);
const TimesheetCheckReport = lazy(
  () => import("src/pages/report/TimesheetCheckReport"),
);
const CreateAnagraficaPage = lazy(
  () => import("src/pages/employee/CreateAnagraficaPage"),
);
const TimesheetPage = lazy(() => import("src/pages/timesheet/TimesheetPage"));
const DocumentsPage = lazy(() => import("src/pages/DocumentsPage"));
const DocumentsSignaturesPage = lazy(
  () => import("src/pages/DocumentsSignaturesPage"),
);
const ScadenzarioPage = lazy(() => import("src/pages/ScadenzarioPage"));
const ConnectApiPage = lazy(() => import("src/pages/ConnectApiPage"));
const PayTransparencyPage = lazy(() => import("src/pages/PayTransparencyPage"));
const PayTransparencyDetailPage = lazy(() => import("src/pages/PayTransparencyDetailPage"));
const AutoPage = lazy(() => import('src/pages/auto/AutoPage'));
const ExpenseReportsPage = lazy(() => import("src/pages/ExpenseReportsPage"));
const ComunicazioniPage = lazy(() => import("src/pages/ComunicazioniPage"));
const HelpDeskPage = lazy(() => import("src/pages/HelpDeskPage"));
const ConfigPage = lazy(() => import("src/pages/ConfigPage"));
const ClockingConnectorPage = lazy(
  () => import("src/pages/config/clocking-connector/ClockingConnectorPage"),
);
const TimesheetConfigPage = lazy(
  () => import("src/pages/config/timesheet/TimesheetConfigPage"),
);
const HolidayPlanConfigPage = lazy(
  () => import("src/pages/config/holiday-plan/HolidayPlanConfigPage"),
);
const ProjectsConfigPage = lazy(
  () => import("src/pages/config/projects/ProjectsConfigPage"),
);
const ExpenseReportsConfigPage = lazy(
  () => import("src/pages/config/expense-reports/ExpenseReportsConfigPage"),
);
const SupervisorsConfigPage = lazy(
  () => import("src/pages/config/supervisors/SupervisorsConfigPage"),
);
const OrgChartPage = lazy(
  () => import("src/pages/config/org-chart/OrgChartPage"),
);
const DocumentTemplatesPage = lazy(
  () => import("src/pages/config/document-templates/DocumentTemplatesPage"),
);
const AccountPage = lazy(() => import("src/pages/AccountPage"));
const CompanyPage = lazy(() => import("src/pages/CompanyPage"));
const StatistichePage = lazy(() => import("src/pages/StatistichePage"));
const LettersPage = lazy(() => import("src/pages/LettersPage"));
const LetterDetailPage = lazy(() => import("src/pages/LetterDetailPage"));
const RequestsPage = lazy(() => import("src/pages/RequestsPage"));
const HolidayPlanPage = lazy(() => import("src/pages/HolidayPlanPage"));
const EmployeePage = lazy(() => import("src/pages/employee/AnagraficaPage"));
const MonthlyTimesheetPage = lazy(
  () => import("src/pages/timesheet/MonthlyTimesheetPage"),
);
const PayrollDataPage = lazy(() => import("src/pages/PayrollDataPage"));
const LaborCostBudgetPage = lazy(() => import("src/pages/LaborCostBudgetPage"));
const ClockingsPage = lazy(() => import("src/pages/ClockingsPage"));
const ClockingPlacesPage = lazy(() => import("../../pages/ClockingPlacesPage"));
const UploadBustePagaPage = lazy(() => import("../../pages/UploadBustePagaPage"));
const ProductUpdatesPage = lazy(() => import("../../pages/ProductUpdatesPage"));
const ProductUpdateDetailPage = lazy(() => import("../../pages/ProductUpdateDetailPage"));
const ProductGuidePage = lazy(() => import("../../pages/ProductGuidePage"));

// ----------------------------------------------------------------------

// Redirect legacy /dashboard/novita/:slug → /dashboard/news/:slug
function NovitaSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={paths.dashboard.news.details(slug)} replace />;
}

// Pagina iniziale. Un Responsabile con il solo accesso alle note spese non ha
// riquadri utili in dashboard (cartellini, richieste e report richiedono
// timesheet_view, requests_view o report_access): apre direttamente le note spese.
// Azienda/Studio/Operatore non sono toccati (hasSupervisorPermission è sempre vero);
// Timesheet e ResponsabileSicurezza hanno riquadri propri e restano in dashboard.
function DashboardIndex() {
  const { user } = useAuthContext();

  if (isExpensesOnlySupervisor(user)) {
    return <Navigate to={paths.dashboard.expenseReports} replace />;
  }

  return <IndexPage />;
}

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: "",
    element: (
      <AuthGuard>
        <DashboardLayout><CompanyOnboardingGuard>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </CompanyOnboardingGuard></DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { path: "onboarding/:companyId", element: <CompanyOnboardingPage /> },
      { element: <DashboardIndex />, index: true },
      {
        path: "employees",
        element: (
          <SupervisorPermissionGuard permission="registry_access">
            <EmployeesPage />
          </SupervisorPermissionGuard>
        ),
      },
      {
        path: "employees/new",
        element: (
          <SupervisorPermissionGuard permission="employees_create">
            <CreateAnagraficaPage />
          </SupervisorPermissionGuard>
        ),
      },
      { path: "employees/:employee_id", element: <EmployeePage /> },
      {
        path: "report",
        element: (
          <SupervisorPermissionGuard permission="report_access">
            <ModuleGuard flag={MODULE_FLAGS.REPORTS}><ReportsPage /></ModuleGuard>
          </SupervisorPermissionGuard>
        ),
      },
      {
        path: "report/employee-security",
        element: (
          <SupervisorPermissionGuard permission="report_access">
            <ModuleGuard flag={MODULE_FLAGS.REPORTS}><EmployeesSecurityReportPage /></ModuleGuard>
          </SupervisorPermissionGuard>
        ),
      },
      {
        path: "report/employee-payroll-data",
        element: (
          <SupervisorPermissionGuard permission="report_access">
            <ModuleGuard flag={MODULE_FLAGS.REPORTS} preview={PREVIEW_FLAGS.EMPLOYEES_RESIDUALS}>
              <EmployeesPayrollDataReportPage />
            </ModuleGuard>
          </SupervisorPermissionGuard>
        ),
      },
      {
        path: "report/employee-documents",
        element: (
          <SupervisorPermissionGuard permission="report_access">
            <ModuleGuard flag={MODULE_FLAGS.REPORTS}><ModuleGuard flag={MODULE_FLAGS.DOCUMENTS}><EmployeeDocumentsReport /></ModuleGuard></ModuleGuard>
          </SupervisorPermissionGuard>
        ),
      },
      {
        path: "report/employee-custom",
        element: (
          <SupervisorPermissionGuard permission="report_access">
            <ModuleGuard flag={MODULE_FLAGS.REPORTS} preview={PREVIEW_FLAGS.CUSTOM_REPORT}><EmployeeCustomReport /></ModuleGuard>
          </SupervisorPermissionGuard>
        ),
      },
      {
        path: "report/timesheet-check",
        element: (
          <SupervisorPermissionGuard permission="report_access">
            <ModuleGuard flag={MODULE_FLAGS.REPORTS}><ModuleGuard flag={MODULE_FLAGS.TIMESHEET}><TimesheetCheckReport /></ModuleGuard></ModuleGuard>
          </SupervisorPermissionGuard>
        ),
      },
      {
        path: "timesheet",
        element: (
          <SupervisorPermissionGuard permission="timesheet_view" orRoles={["Timesheet"]}>
            <ModuleGuard flag={MODULE_FLAGS.TIMESHEET}><TimesheetPage /></ModuleGuard>
          </SupervisorPermissionGuard>
        ),
      },
      {
        path: "timesheet/:year/:month",
        element: (
          <SupervisorPermissionGuard permission="timesheet_view" orRoles={["Timesheet"]}>
            <ModuleGuard flag={MODULE_FLAGS.TIMESHEET}><TimesheetPage /></ModuleGuard>
          </SupervisorPermissionGuard>
        ),
      },
      {
        path: "timesheet/:year/:month/:employee_id",
        element: (
          <SupervisorPermissionGuard permission="timesheet_view" orRoles={["Timesheet"]}>
            <ModuleGuard flag={MODULE_FLAGS.TIMESHEET}><MonthlyTimesheetPage /></ModuleGuard>
          </SupervisorPermissionGuard>
        ),
      },
      { path: "documents", element: <ModuleGuard flag={MODULE_FLAGS.DOCUMENTS}><DocumentsPage /></ModuleGuard> },
      { path: "documents/signatures", element: <ModuleGuard flag={MODULE_FLAGS.DOCUMENTS}><DocumentsSignaturesPage /></ModuleGuard> },
      {
        path: "timetable",
        element: (
          <SupervisorPermissionGuard permission="scadenzario_access">
            <ModuleGuard flag={MODULE_FLAGS.DEADLINES}><ScadenzarioPage /></ModuleGuard>
          </SupervisorPermissionGuard>
        ),
      },
      {
        path: "pay-transparency",
        element: (
          <ModuleGuard flag={MODULE_FLAGS.REPORTS} preview={PREVIEW_FLAGS.PAY_TRANSPARENCY}>
            <PayTransparencyPage />
          </ModuleGuard>
        ),
      },
      {
        path: "connect-api",
        element: (
          <ModuleGuard preview={PREVIEW_FLAGS.CONNECT_API}>
            <ConnectApiPage />
          </ModuleGuard>
        ),
      },
      {
        path: "pay-transparency/:reqId",
        element: (
          <ModuleGuard flag={MODULE_FLAGS.REPORTS} preview={PREVIEW_FLAGS.PAY_TRANSPARENCY}>
            <PayTransparencyDetailPage />
          </ModuleGuard>
        ),
      },
      { path: "auto", element: <AutoPage /> },
      { path: "note-spese", element: <ExpenseReportsPage /> },
      { path: "comunicazioni", element: <ModuleGuard flag={MODULE_FLAGS.COMMUNICATIONS}><ComunicazioniPage /></ModuleGuard> },
      { path: "letters", element: <ModuleGuard flag={MODULE_FLAGS.COMMUNICATIONS}><LettersPage /></ModuleGuard> },
      { path: "letters/:letter_id", element: <ModuleGuard flag={MODULE_FLAGS.COMMUNICATIONS}><LetterDetailPage /></ModuleGuard> },
      { path: "help-desk", element: <HelpDeskPage /> },
      { path: "stats", element: <ModuleGuard flag={MODULE_FLAGS.TIMESHEET}><StatistichePage /></ModuleGuard> },
      {
        path: "config/custom-fields",
        element: (
          <RoleGuard roles="Azienda">
            <EmployeeCustomFieldsConfigPage />
          </RoleGuard>
        ),
      },
      { path: "config", element: <ConfigPage /> },
      { path: "config/timesheet", element: <ModuleGuard flag={MODULE_FLAGS.TIMESHEET}><TimesheetConfigPage /></ModuleGuard> },
      { path: "config/holiday-plan", element: <HolidayPlanConfigPage /> },
      { path: "config/projects", element: <ModuleGuard flag={MODULE_FLAGS.PROJECTS}><ProjectsConfigPage /></ModuleGuard> },
      {
        path: "config/expense-reports",
        element: (
          <SupervisorPermissionGuard permission="expenses_config">
            <ExpenseReportsConfigPage />
          </SupervisorPermissionGuard>
        ),
      },
      { path: "config/supervisors", element: <SupervisorsConfigPage /> },
      { path: "config/clocking-places", element: <ModuleGuard flag={MODULE_FLAGS.TIMESHEET}><ClockingPlacesPage /></ModuleGuard> },
      {
        path: "config/connettore-timbrature",
        element: (
          <RoleGuard roles="Azienda">
            <ClockingConnectorPage />
          </RoleGuard>
        ),
      },
      { path: "config/org-chart", element: <ModuleGuard preview={PREVIEW_FLAGS.ORG_CHART}><OrgChartPage /></ModuleGuard> },
      { path: "config/document-templates", element: <ModuleGuard flag={MODULE_FLAGS.DOCUMENTS}><ModuleGuard flag={MODULE_FLAGS.DOCUMENT_GENERATION}><DocumentTemplatesPage /></ModuleGuard></ModuleGuard> },
      { path: "company", element: <CompanyPage /> },
      { path: "account", element: <AccountPage /> },
      {
        path: "requests",
        element: (
          <SupervisorPermissionGuard permission="requests_view" orRoles={["Timesheet"]}>
            <RequestsPage />
          </SupervisorPermissionGuard>
        ),
      },
      { path: "holiday-plan", element: <HolidayPlanPage /> },
      {
        path: "news",
        element: (
          <RoleGuard roles="Azienda">
            <ProductUpdatesPage />
          </RoleGuard>
        ),
      },
      {
        path: "news/guide/:slug",
        element: (
          <RoleGuard roles="Azienda">
            <ProductGuidePage />
          </RoleGuard>
        ),
      },
      {
        path: "news/:slug",
        element: (
          <RoleGuard roles="Azienda">
            <ProductUpdateDetailPage />
          </RoleGuard>
        ),
      },
      { path: "faq", element: <FAQPage /> },
      { path: "clockings", element: <ModuleGuard flag={MODULE_FLAGS.TIMESHEET}><ClockingsPage /></ModuleGuard> },
      { path: "payroll-data", element: <ModuleGuard flag={MODULE_FLAGS.PAYROLL_DATA}><PayrollDataPage /></ModuleGuard> },
      {
        path: "labor-cost-budget",
        element: (
          <ModuleGuard preview={PREVIEW_FLAGS.LABOR_COST_BUDGET}>
            <RoleGuard roles="Azienda">
              <LaborCostBudgetPage />
            </RoleGuard>
          </ModuleGuard>
        ),
      },
      { path: "upload-buste-paga", element: <ModuleGuard flag={MODULE_FLAGS.DOCUMENTS}><UploadBustePagaPage /></ModuleGuard> },
      { path: "novita", element: <Navigate to={paths.dashboard.news.root} replace /> },
      { path: "novita/:slug", element: <NovitaSlugRedirect /> },
      // { path: 'mail', element: <MailPage /> },
      // { path: 'chat', element: <ChatPage /> },
      // { path: 'calendar', element: <CalendarPage /> },
      // { path: 'kanban', element: <KanbanPage /> },
      // { path: 'permission', element: <PermissionDeniedPage /> },
      // { path: 'blank', element: <BlankPage /> },
      // { path: 'booking', element: <OverviewBookingPage /> },
      // { path: 'file', element: <OverviewFilePage /> },
      // {
      //   path: 'user',
      //   children: [
      //     { element: <UserProfilePage />, index: true },
      //     { path: 'profile', element: <UserProfilePage /> },
      //     { path: 'cards', element: <UserCardsPage /> },
      //     { path: 'list', element: <UserListPage /> },
      //     { path: 'new', element: <UserCreatePage /> },
      //     { path: ':id/edit', element: <UserEditPage /> },
      //     { path: 'account', element: <UserAccountPage /> },
      //   ],
      // },
      // {
      //   path: 'product',
      //   children: [
      //     { element: <ProductListPage />, index: true },
      //     { path: 'list', element: <ProductListPage /> },
      //     { path: ':id', element: <ProductDetailsPage /> },
      //     { path: 'new', element: <ProductCreatePage /> },
      //     { path: ':id/edit', element: <ProductEditPage /> },
      //   ],
      // },
      // {
      //   path: 'order',
      //   children: [
      //     { element: <OrderListPage />, index: true },
      //     { path: 'list', element: <OrderListPage /> },
      //     { path: ':id', element: <OrderDetailsPage /> },
      //   ],
      // },
      // {
      //   path: 'invoice',
      //   children: [
      //     { element: <InvoiceListPage />, index: true },
      //     { path: 'list', element: <InvoiceListPage /> },
      //     { path: ':id', element: <InvoiceDetailsPage /> },
      //     { path: ':id/edit', element: <InvoiceEditPage /> },
      //     { path: 'new', element: <InvoiceCreatePage /> },
      //   ],
      // },
      // {
      //   path: 'post',
      //   children: [
      //     { element: <BlogPostsPage />, index: true },
      //     { path: 'list', element: <BlogPostsPage /> },
      //     { path: ':title', element: <BlogPostPage /> },
      //     { path: ':title/edit', element: <BlogEditPostPage /> },
      //     { path: 'new', element: <BlogNewPostPage /> },
      //   ],
      // },
      // {
      //   path: 'job',
      //   children: [
      //     { element: <JobListPage />, index: true },
      //     { path: 'list', element: <JobListPage /> },
      //     { path: ':id', element: <JobDetailsPage /> },
      //     { path: 'new', element: <JobCreatePage /> },
      //     { path: ':id/edit', element: <JobEditPage /> },
      //   ],
      // },
      // {
      //   path: 'tour',
      //   children: [
      //     { element: <TourListPage />, index: true },
      //     { path: 'list', element: <TourListPage /> },
      //     { path: ':id', element: <TourDetailsPage /> },
      //     { path: 'new', element: <TourCreatePage /> },
      //     { path: ':id/edit', element: <TourEditPage /> },
      //   ],
      // },
    ],
  },
];
